import Dinero from 'dinero.js';
import { Dictionary, keyBy, round } from 'lodash';
import { DateTime, Duration, Interval } from 'luxon';
import { useContext, useEffect, useState } from 'react';
import {
  CalculateFinancialSummaryRequest,
  FinancialSummary,
} from '../../../../clients/quipli-service/financials/financials';
import { Invoice } from '../../../../clients/quipli-service/invoice/invoice';
import { v4 as uuidv4 } from 'uuid';
import { useInvoiceApiV1 } from '../../../../clients/quipli-service/invoice/invoice-api-v1';
import {
  CanOffRentValidationError,
  InventoryReturnRequest,
  OffRentRequest,
  Order,
  OrderProduct,
  OrderReturnStatus,
  OrderType,
} from '../../../../clients/quipli-service/order/order';
import { useOrderApiV1 } from '../../../../clients/quipli-service/order/order-api-v1';
import {
  Payment,
  PaymentMethodType,
  PaymentReason,
  PaymentStatus,
} from '../../../../clients/quipli-service/payment/payment';
import { usePaymentApiV2 } from '../../../../clients/quipli-service/payment/payment-api-v2';
import { PaymentMethodRequestV2 } from '../../../../clients/quipli-service/payment/payment-v2';
import { RentalProductPrice } from '../../../../clients/quipli-service/pricing/pricing';
import { usePricingApiV1 } from '../../../../clients/quipli-service/pricing/pricing-api-v1';
import { ProductType } from '../../../../clients/quipli-service/product/product';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { Button } from '../../../../components/form/button';
import { Modal } from '../../../../components/modal/modal';
import { Spinner } from '../../../../components/spinner/spinner';
import { ValidationError } from '../../../../components/validation-error/validation-error';
import { TenantContext } from '../../../../context/tenant-context';
import { orderToPaymentMethodV2, StatusTransitionPayment } from '../status-transition-payment';
import { toast } from '../../../../util/toasts';
import { Location } from '../../../../clients/quipli-service/location/location';
import { Configuration } from '../../../../clients/quipli-service/configuration/configuration';
import { PartialOffRentStep } from './partial-offrent-step';
import { DateTimePicker } from '../../../../components/date-picker/date-time-picker';
import classNames from 'classnames';
import { EditableField } from '../../../../components/field/field';
import { Toggle } from '../../../../components/form/toggle';
import {
  inventoryReturnCalculations,
  InventoryReturnFuelChargeInput,
  InventoryReturnHourInput,
  useHourOverageCalculation,
} from '../inventory-return';
import Icon from '../../../../components/icons/icon';
import './off-rent.scss';
import { Typography } from '../../../../components/typography/typography';
import { Checkbox } from '../../../../components/checkbox/checkbox';
import {
  financialLineItemTypeToRank,
  getUpdatedItemRequestsFromFuelCharges,
  isSameLine,
  offRentCalculations,
  orderToCalculateFinancialSummaryRequest,
  toUpdateOrderRequest,
} from './utils/off-rent-utils';

export type FuelCharge = {
  orderProductId: number;
  productId?: number;
  unitId: number;
  amount: number;
  uuid: string;
};

export const OffRentOrder = ({
  orderId,
  onCancel,
  onComplete,
}: {
  orderId: number;
  onCancel: () => void;
  onComplete: () => void;
}) => {
  const { configuration } = useContext(TenantContext);
  const { calculateFinancialSummary, getMultipleConfigurations, listLocations } = useAPI();
  const { offRentOrder, inventoryReturn, getOrderById, updateOrder, canOffRentOrder } = useOrderApiV1();
  const { createLineItem } = useInvoiceApiV1();
  const { createPayment } = usePaymentApiV2();
  const { listPrices } = usePricingApiV1();
  const [isOffRenting, setIsOffRenting] = useState<boolean>(false);
  const [order, setOrder] = useState<Order>();
  const [invoice, setInvoice] = useState<Invoice | undefined>();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodRequestV2 | undefined>();
  const [offRentWithPayment, setOffRentWithPayment] = useState<boolean>(true);
  const [amount, setAmount] = useState<string>();
  const [offRentRequest, setOffRentRequest] = useState<OffRentRequest>();
  const [inventoryReturnRequest, setInventoryReturnRequest] = useState<InventoryReturnRequest>();

  // Fuel Charge Portions
  const [fuelCharges, setFuelCharges] = useState<FuelCharge[]>([]);

  const [lateChargeSummaryRequest, setLateChargeSummaryRequest] = useState<CalculateFinancialSummaryRequest>();
  const [lateChargeSummary, setLateChargeSummary] = useState<FinancialSummary>();
  const [existingSummaryRequest, setExistingSummaryRequest] = useState<CalculateFinancialSummaryRequest>();
  const [existingSummary, setExistingSummary] = useState<FinancialSummary>();
  const currency = configuration ? (configuration.currency as Dinero.Currency) : undefined;
  const [customPrices, setCustomPrices] = useState<RentalProductPrice[]>();
  const [locations, setLocations] = useState<Location[]>();
  const [unitTrackingType, setUnitTrackingType] = useState<'serialNumber' | 'unitNumber'>('unitNumber');
  const [payments, setPayments] = useState<Payment[]>();
  const [facilitate, setFacilitate] = useState<boolean>(false);
  const [configs, setConfigs] = useState<Dictionary<Configuration>>();
  const [waiveLateCharge, setWaiveLateCharge] = useState<boolean>(false);
  const [showSelectProductsStep, setShowSelectProductsStep] = useState<boolean>(true);
  const [onOffRentUpdateDateConfiguration, setOnOffRentUpdateDateConfiguration] = useState<boolean>(false);
  const [onOffRentUpdateDateCheckbox, setOnOffRentUpdateDateCheckbox] = useState<boolean>(true);
  const [canOffRent, setCanOffRent] = useState<CanOffRentValidationError[]>();

  const { hourOverages, hourOverageOverrides, setHourOverageOverrides, isCalculating } = useHourOverageCalculation(
    inventoryReturnRequest,
    order,
  );

  // TODO: needs loading indicators
  useEffect(() => {
    const init = async () => {
      getOrderById(orderId)
        .then(setOrder)
        .catch((e) => toast(e.message, 'error'));
      getMultipleConfigurations([
        'CONF_DEFAULT_RENTAL_END_TIME',
        'CONF_UNIT_TRACKING_VIA',
        'CONF_ALLOW_COMBINED_OFF_RENT_RETURN',
        'CONF_GRACE_PERIOD_FOR_RETURNS',
        'CONF_SEIS_INTEGRATION',
        'CONF_FUEL_UNIT',
        'CONF_ON_OFF_RENT_UPDATE_ORDER_DATE',
      ])
        .then((confs) => {
          setUnitTrackingType(
            confs.find((c) => c.variable === 'CONF_UNIT_TRACKING_VIA')?.value === '1' ? 'serialNumber' : 'unitNumber',
          );
          setConfigs(keyBy(confs, 'variable'));
          setOnOffRentUpdateDateConfiguration(
            confs.find((c) => c.variable === 'CONF_ON_OFF_RENT_UPDATE_ORDER_DATE')?.value?.includes('offrent') ?? false,
          );
        })
        .catch((e) => {
          console.log(e);
          toast('Error loading configurations', 'error');
        });
      listLocations()
        .then(setLocations)
        .catch((e) => {
          console.log(e);
          toast('Error loading locations', 'error');
        });
    };
    init();
  }, []);

  // TODO: this should be part of the init call, doesn't need its own use effect
  useEffect(() => {
    if (order) {
      listPrices({
        productIds: order.products.filter((op) => op.type === OrderType.Rental).map((op) => op.product.id),
        locationId: order.locationId,
        orderId: order.id,
        type: OrderType.Rental,
      })
        .then((prices) => {
          const customPrices = prices.flatMap((p) => {
            const rp = p as RentalProductPrice;
            if (rp.orderId) {
              return [rp];
            } else {
              return [];
            }
          });
          setCustomPrices(customPrices);
        })
        .catch((e) => {
          console.log(e);
          toast('Error loading custom rental rates', 'error');
        });
    }
  }, [order]);

  // TODO: this should be part of the init call, doesn't need its own use effect
  useEffect(() => {
    if (order && configs && configs['CONF_ALLOW_COMBINED_OFF_RENT_RETURN']?.value === '1') {
      const orderProducts = order.products.filter(
        (op) => op.type === OrderType.Rental && op.status === OrderReturnStatus.OnRent,
      );
      if (showSelectProductsStep) {
        setInventoryReturnRequest({
          orderProductIds: orderProducts.filter((op) => op.status === OrderReturnStatus.OnRent).map((op) => op.id),
          returnToLocation: order.locationId,
          hourReadings: orderProducts
            .filter((op) => op.product.allowHourTracking)
            .flatMap((op) => op.assignedUnits)
            .map((au) => {
              return { unitId: au.id, hourReading: au.lastHourReading?.hour || 0 };
            }),
          chargeSecurityDeposit: false,
        });
      }
    }
  }, [configs, order]);

  useEffect(() => {
    setExistingSummaryRequest((prev) => {
      if (!offRentRequest) {
        return prev;
      }

      const updatedItemRequests = getUpdatedItemRequestsFromFuelCharges(fuelCharges, offRentRequest, prev);

      if (prev) {
        return {
          ...prev,
          items: updatedItemRequests,
        };
      }
      return prev;
    });
  }, [fuelCharges]);

  useEffect(() => {
    if (
      !showSelectProductsStep &&
      order &&
      offRentRequest &&
      ((!order.isCycleBilled && customPrices) || fuelCharges) &&
      existingSummaryRequest
    ) {
      canOffRentOrder(orderId, offRentRequest)
        .then(setCanOffRent)
        .catch((e) => toast(e.message, 'error'));

      const lateRequest = {
        ...existingSummaryRequest,
        items: [
          ...existingSummaryRequest.items.map((item) => {
            if (
              item.type === OrderType.Rental &&
              offRentRequest.offRentProducts.map((orp) => orp.productId).includes(parseInt(item.id)) &&
              configs &&
              configs['CONF_GRACE_PERIOD_FOR_RETURNS'] &&
              (!item.end ||
                item.end.plus(
                  Duration.fromObject({ hours: parseFloat(configs['CONF_GRACE_PERIOD_FOR_RETURNS']?.value) }),
                ) < offRentRequest.timestamp ||
                (!order.isCycleBilled && onOffRentUpdateDateCheckbox && onOffRentUpdateDateConfiguration))
            ) {
              return {
                ...item,
                end: offRentRequest.timestamp,
              };
            }
            return item;
          }),
        ],
      };
      setLateChargeSummaryRequest(lateRequest);
    }
  }, [order, offRentRequest, customPrices, existingSummaryRequest]);

  // This effect is used to recalculate the late charge summary when the checkbox is toggled
  useEffect(() => {
    setLateChargeSummaryRequest((prev) => {
      if (prev) {
        // If unchecked, set the end date to the rental end date so summary recalculates
        if (!onOffRentUpdateDateCheckbox) {
          const newItems = prev.items.map((item) => {
            if (item.type === OrderType.Rental) {
              return {
                ...item,
                end: order?.products.find((op) => op.id === parseInt(item.id))?.rentalEndDate,
              };
            }
            return item;
          });
          return {
            ...prev,
            items: newItems,
          };
        } else {
          return {
            ...prev,
            items: prev.items.map((item) => {
              if (item.type === OrderType.Rental) {
                return {
                  ...item,
                  end: offRentRequest?.timestamp,
                };
              }
              return item;
            }),
          };
        }
      }
    });
  }, [onOffRentUpdateDateCheckbox]);

  // This effect is used to recalculate the late charge summary when the request changes
  useEffect(() => {
    if (lateChargeSummaryRequest) {
      calculateFinancialSummary(lateChargeSummaryRequest)
        .then((fs) => setLateChargeSummary(fs))
        .catch((e) => {
          console.log(e);
          toast('Error calculating financial summary', 'error');
        });
    }
  }, [lateChargeSummaryRequest]);

  useEffect(() => {
    if (invoice) {
      if (invoice.balance <= 0) {
        setOffRentWithPayment(false);
      }
    }
  }, [invoice]);

  const computeUpdatedOrderPricing = async () => {
    if (order && customPrices) {
      const newPrices = await listPrices({
        productIds: order.products.filter((op) => op.type === OrderType.Rental).map((op) => op.product.id),
        locationId: order.locationId,
        orderId: order.id,
        type: OrderType.Rental,
      });

      const newCustomPrices = newPrices.flatMap((p) => {
        const rp = p as RentalProductPrice;
        if (rp.orderId) {
          return [rp];
        } else {
          return [];
        }
      });

      if (order.paymentMethod.type === PaymentMethodType.OnAccount) {
        setOffRentWithPayment(false);
      }
      const orderProducts = order.products.filter(
        (op) => op.type === OrderType.Rental && (!op.status || op.status === OrderReturnStatus.OnRent),
      );
      if (orderProducts.length > 0) {
        let earliestTime = undefined;
        for (const op of orderProducts) {
          if (!earliestTime) {
            earliestTime = op.actualRentalStartDate ?? op.rentalStartDate;
          } else {
            earliestTime = DateTime.min(earliestTime, op.actualRentalStartDate ?? op.rentalStartDate);
          }
        }

        const request = {
          offRentProducts: offRentRequest?.offRentProducts.map((orp) => orp.productId)?.length
            ? offRentRequest.offRentProducts
            : orderProducts.flatMap((op) =>
                op.type === OrderType.Rental && op.status === OrderReturnStatus.OnRent
                  ? { productId: op.id, type: op.trackingType, quantity: op.quantity ?? undefined }
                  : [],
              ),
          timestamp: onOffRentUpdateDateConfiguration
            ? DateTime.max(
                earliestTime ?? DateTime.fromMillis(0),
                DateTime.local({ zone: orderProducts.find((op) => op.rentalStartDate?.zone)?.rentalStartDate.zone }),
              )
            : offRentRequest?.timestamp
            ? offRentRequest.timestamp
            : DateTime.min(
                ...orderProducts.flatMap((op) =>
                  op.rentalEndDate &&
                  (configs && (configs['CONF_DEFAULT_RENTAL_END_TIME']?.value as 'RentalEnd' | 'CurrentTime')) ===
                    'RentalEnd'
                    ? [op.rentalEndDate]
                    : order.isCycleBilled && op.type === OrderType.Rental
                    ? [op.rentalStartDate.plus(order.cycleBillingTerms!.cycleLength)]
                    : [],
                ),
                ...((configs && (configs['CONF_DEFAULT_RENTAL_END_TIME']?.value as 'RentalEnd' | 'CurrentTime')) ===
                'CurrentTime'
                  ? [
                      DateTime.local({
                        zone: orderProducts.find((op) => op.rentalStartDate?.zone)?.rentalStartDate.zone,
                      }),
                    ]
                  : []),
              ),
        } as OffRentRequest;
        setOffRentRequest(request);
        setExistingSummaryRequest(orderToCalculateFinancialSummaryRequest(order, newCustomPrices));
        setPaymentMethod(orderToPaymentMethodV2(order));
        setInvoice(order.invoices.find((inv) => inv));
      }
    }
  };

  useEffect(() => {
    computeUpdatedOrderPricing();
  }, [order, customPrices]);

  // This effect is used to recalculate the financial summary when the existing summary request changes
  useEffect(() => {
    if (existingSummaryRequest && order) {
      calculateFinancialSummary(existingSummaryRequest).then((fs) => {
        const customPriceProducts = order.products.filter((op) =>
          fs.lineItems.some(
            (li) => li.type === 'Product' && li.id === `${op.id}` && li.unitPrice.toUnit() !== op.customerBuyingPrice,
          ),
        );
        if (customPriceProducts.length > 0) {
          const requestWithCustomPrices = {
            ...existingSummaryRequest,
            items: existingSummaryRequest.items.map((i) => {
              const op = customPriceProducts.find((op) => i.id === `${op.id}`);
              if (op) {
                return { ...i, customPrice: round(op.customerBuyingPrice * 100) };
              } else {
                return i;
              }
            }),
          };
          setExistingSummaryRequest(requestWithCustomPrices);
        } else {
          setExistingSummary(fs);
        }
      });
    }
  }, [existingSummaryRequest, order]);

  async function postPayment(payments?: Payment[]) {
    setFacilitate(false);
    if (payments) {
      if (payments[0].gatewayStatus === 'requires_action') {
        toast('Payment requires authentication', 'error');
      } else if (payments[0].status === 'failed') {
        toast('Payment failed', 'error');
      }
      setIsOffRenting(false);
      onComplete();
    }
  }

  async function doOffRent() {
    const anyFuelCharges = fuelCharges?.some((fc) => fc.amount > 0);
    if (offRentRequest && order && (customPrices || anyFuelCharges)) {
      try {
        setIsOffRenting(true);
        if (
          !waiveLateCharge &&
          existingSummary &&
          lateChargeSummary &&
          !existingSummary.total.equalsTo(lateChargeSummary.total) &&
          currency
        ) {
          await updateOrder(
            order.id,
            toUpdateOrderRequest(order, customPrices ?? [], offRentRequest, lateChargeSummary, currency),
          );
        } else if (existingSummary && currency && anyFuelCharges) {
          await updateOrder(
            order.id,
            toUpdateOrderRequest(order, customPrices ?? [], offRentRequest, existingSummary, currency),
          );
        }
        await offRentOrder(orderId, offRentRequest);

        if (inventoryReturnRequest && inventoryReturnRequest.orderProductIds.length > 0) {
          if (!waiveLateCharge && invoice) {
            await Promise.all(
              hourOverageOverrides
                ?.filter(
                  (ov) => ov.overageCharge > 0 && inventoryReturnRequest.orderProductIds.includes(ov.orderProductId),
                )
                .map((ov) => {
                  const hourOverage = hourOverages!.find(
                    (ho) => ho.orderProductId === ov.orderProductId && ho.unitId === ov.unitId,
                  )!;
                  const op = order.products.find((op) => op.id === ov.orderProductId)!;
                  const unit = op.assignedUnits.find((au) => ov.unitId === au.id)!;
                  const unitPrice = round(ov.overageCharge / hourOverage.overageHours / 100, 2);
                  return createLineItem(invoice.id, {
                    name: `Hour overage ${op.product.name} - ${unit[unitTrackingType]}`,
                    description: `${hourOverage.overageHours} hours @ ${unitPrice.toFixed(2)}/hr`,
                    unitPrice,
                    quantity: hourOverage.overageHours,
                    taxable: op.product.taxable,
                  });
                }),
            );
          }
          await inventoryReturn(orderId, inventoryReturnRequest);
        }

        if (offRentWithPayment && invoice && paymentMethod && amount && parseFloat(amount) > 0) {
          const payment = await createPayment({
            invoiceIds: [invoice.id],
            paymentMethod,
            amount: parseFloat(amount),
            userId: invoice.customer.id,
          });
          if (!payment) {
            toast('Payment failed', 'error');
            return;
          }

          if (paymentMethod.type === PaymentMethodType.Stripe && paymentMethod.terminalId) {
            setFacilitate(true);
            setPayments([payment]);
            return;
          } else {
            if (payment.gatewayStatus === 'requires_action') {
              toast('Payment requires authentication', 'error');
            } else if (payment.status === 'failed') {
              toast('Payment failed', 'error');
              return;
            }
            onComplete();
          }
        } else {
          onComplete();
        }
      } catch (err: any) {
        toast(err.message, 'error');
      } finally {
        if (!(paymentMethod?.type === PaymentMethodType.Stripe && paymentMethod?.terminalId)) {
          setIsOffRenting(false);
        }
      }
    }
  }

  const {
    hourOverageTax,
    adhocLineItems,
    adHocLineItemsTotal,
    paymentsTotal,
    lateChargeTotal,
    total,
    balance,
    paymentGreaterThanBalance,
    isValid,
  } = offRentCalculations({
    order,
    currency,
    hourOverageOverrides,
    inventoryReturnRequest,
    existingSummary,
    lateChargeSummary,
    waiveLateCharge,
    amount,
    offRentRequest,
  });

  const cycleBillingReturnTotals = inventoryReturnCalculations({
    financialSummary: lateChargeSummary,
    order,
    currency: currency ?? 'USD',
    hourOverageOverrides,
    amount,
    invoice,
    inventoryReturnRequest,
    canInventoryReturn: canOffRent,
  });

  const orderValidationMessages = [...(cycleBillingReturnTotals.orderValidationMessages ?? [])];
  if (paymentGreaterThanBalance) {
    orderValidationMessages.push(`Cannot collect more than the order balance of ${balance.toFormat()}`);
  }
  const noProductsToOffRent =
    order && !order?.products.some((op) => op.type === OrderType.Rental && op.status === OrderReturnStatus.OnRent);
  const showInventoryReturn = configs && configs['CONF_ALLOW_COMBINED_OFF_RENT_RETURN']?.value === '1';

  return (
    <Modal
      isOpen={true}
      title="Off Rent Order"
      actions={
        !order || isOffRenting || showSelectProductsStep ? null : (
          <>
            <Button className="button button-secondary" onClick={() => onCancel()}>
              Cancel
            </Button>
            <Button
              className="button button-yellow"
              disabled={!isValid || orderValidationMessages.length > 0}
              onClick={() => doOffRent()}
            >
              Off Rent
            </Button>
          </>
        )
      }
      close={() => onCancel()}
      className="off-rent-order-modal overflow-scroll w-full"
    >
      {noProductsToOffRent ? (
        <ValidationError>
          <ul>
            <li>No remaining products to off rent</li>
          </ul>
        </ValidationError>
      ) : // loading state...
      !(order && offRentRequest && configs && locations) ||
        (isOffRenting && !(paymentMethod?.type == PaymentMethodType.Stripe && paymentMethod.terminalId)) ? (
        <Spinner />
      ) : // select products/units screen
      showSelectProductsStep ? (
        <>
          <PartialOffRentStep
            order={order}
            setOrder={setOrder}
            configs={configs}
            offRentRequest={offRentRequest}
            setOffRentRequest={setOffRentRequest}
            hourOverages={hourOverages}
            hourOverageOverrides={hourOverageOverrides}
            setHourOverageOverrides={setHourOverageOverrides}
            isHourOverageCalculating={isCalculating}
            locations={locations ?? []}
            inventoryReturnRequest={inventoryReturnRequest}
            setInventoryReturnRequest={setInventoryReturnRequest}
            onComplete={() => {
              setShowSelectProductsStep(false);
            }}
            onCancel={onCancel}
            customPrices={customPrices}
            fuelCharges={fuelCharges}
            setFuelCharges={setFuelCharges}
          />
        </>
      ) : (
        offRentRequest && (
          <div className="confirm-off-rent">
            <div className="flex flex-column w-full gap-2">
              <div className="gap-2 flex flex-column">
                <h6 className="m-0">Off Rent At</h6>
                <DateTimePicker
                  value={offRentRequest.timestamp}
                  onChange={(d) =>
                    d &&
                    setOffRentRequest((r) => {
                      return !r ? undefined : { ...r, timestamp: d };
                    })
                  }
                  timezone={offRentRequest.timestamp?.zone.name}
                  disabledIntervals={[
                    Interval.fromDateTimes(
                      DateTime.fromMillis(0),
                      order.products
                        .filter((op) => offRentRequest.offRentProducts.map((orp) => orp.productId).includes(op.id))
                        .reduce((prev, cur) => {
                          return !prev && cur.actualRentalStartDate
                            ? cur
                            : !prev && !cur.actualRentalStartDate
                            ? undefined
                            : !cur.actualRentalStartDate
                            ? prev
                            : cur.actualRentalStartDate!.diff(prev!.actualRentalStartDate!).as('millisecond') < 0
                            ? cur
                            : prev;
                        }, undefined as OrderProduct | undefined)?.actualRentalStartDate ?? DateTime.fromMillis(0),
                    ),
                  ]}
                />
                {!order.isCycleBilled && onOffRentUpdateDateConfiguration && (
                  <Checkbox
                    label="Update product end with off-rent date/time"
                    isChecked={onOffRentUpdateDateCheckbox}
                    onChange={setOnOffRentUpdateDateCheckbox}
                  />
                )}
                {showInventoryReturn && inventoryReturnRequest?.orderProductIds?.length && locations ? (
                  <Typography className="my-2" type="text-sm" weight="medium">
                    Returning to: {locations.find((l) => l.id === inventoryReturnRequest.returnToLocation)?.name}
                  </Typography>
                ) : (
                  <></>
                )}
                {configs['CONF_SEIS_INTEGRATION_ENABLED']?.value === '1' &&
                  offRentRequest.timestamp.diffNow().as('hours') < 0 && (
                    // TODO: check if there are even any seis certs for order
                    <ValidationError className="warning">
                      <ul>
                        <li>
                          If applicable, rental damage waiver insurance cannot be updated automatically if the off rent
                          date is after the rental end date because the RDW has already expired. You must contact SEIS
                          directly to correct this.
                        </li>
                      </ul>
                    </ValidationError>
                  )}
                {!order.isCycleBilled && (
                  <div className="flex gap-2 text-[0.9rem] font-bold items-center py-2">
                    <Toggle
                      onClick={() => {
                        setWaiveLateCharge((prev) => !prev);
                      }}
                      checked={waiveLateCharge}
                    />
                    Waive Late Charge
                  </div>
                )}
              </div>

              {showInventoryReturn &&
              inventoryReturnRequest?.orderProductIds?.length &&
              order.payments.find(
                (op) => op.reason === PaymentReason.SecurityDeposit && op.status === PaymentStatus.pending,
              ) ? (
                <div className="flex gap-2 text-[0.9rem] font-bold items-center py-2">
                  <Toggle
                    onClick={() => {
                      setInventoryReturnRequest(
                        (prev) => prev && { ...prev, chargeSecurityDeposit: !prev.chargeSecurityDeposit },
                      );
                    }}
                    checked={
                      inventoryReturnRequest.orderProductIds.length > 0 && inventoryReturnRequest.chargeSecurityDeposit
                    }
                    disabled={inventoryReturnRequest.orderProductIds.length === 0}
                  />
                  Charge Security Deposit
                </div>
              ) : (
                <></>
              )}

              <div className="w-full border-solid border-[var(--grey-300)]"></div>
              {/* -------------------- start of grid ------------------------ */}

              <div>
                <div className={`grid grid-cols-6 gap-4 text-[0.9rem] min-w-[525px]`}>
                  {/* col 1 & 2 */}
                  <label className={`col-span-2`}>Products</label>
                  {/* col 3 */}
                  {showInventoryReturn && inventoryReturnRequest?.orderProductIds?.length ? (
                    <label>Return</label>
                  ) : (
                    <div></div>
                  )}
                  <>
                    {/* col 4 */}
                    {!order.isCycleBilled ? (
                      <div>
                        <label>Original Price</label>
                      </div>
                    ) : (
                      <div></div>
                    )}

                    {/* col 5 */}
                    <label>
                      {order.isCycleBilled &&
                      (fuelCharges.some((fc) => fc.amount > 0) || hourOverages?.some((ho) => ho.overageCharge > 0))
                        ? 'Total'
                        : order.isCycleBilled
                        ? ''
                        : 'Late Charge'}
                    </label>

                    {/* col 6 */}
                    <label>{order.isCycleBilled ? '' : 'Total'}</label>
                  </>
                  {/* ------------ row break ------------ */}{' '}
                  {order.products
                    .filter((op) => offRentRequest.offRentProducts.map((orp) => orp.productId).includes(op.id))
                    .map((op) => {
                      const isSelectedForReturn = inventoryReturnRequest?.orderProductIds.includes(op.id);
                      const existingAmount = existingSummary?.lineItems.find(
                        (li) =>
                          ((op.product.type === ProductType.Custom && li.type === 'CustomProduct') ||
                            li.type == 'Product') &&
                          li.id === op.id.toString(),
                      )?.amount;
                      const lateChargeAmount = lateChargeSummary?.lineItems.find(
                        (li) =>
                          ((op.product.type === ProductType.Custom && li.type === 'CustomProduct') ||
                            li.type == 'Product') &&
                          li.id === op.id.toString(),
                      )?.amount;
                      const value = lateChargeAmount && existingAmount && lateChargeAmount.subtract(existingAmount);
                      return (
                        <>
                          {/* col 1-2*/}
                          <div className={`col-span-2`}>
                            {op.product.type === ProductType.Custom ? op.name : op.product.name}
                          </div>

                          <>
                            {/* col 3 */}
                            {isSelectedForReturn ? (
                              <Icon icon="Check" className="text-[var(--grey-200)]" />
                            ) : (
                              <div></div>
                            )}

                            {/* col 4 */}
                            <div key={`${op.id}-original`} className=" items-center gap-2">
                              {!order?.isCycleBilled && existingAmount?.toFormat()}
                            </div>

                            {/* col 5 */}
                            <div key={`${op.id}-late`} className="items-center gap-2">
                              {waiveLateCharge ? (
                                <span className="text-[var(--grey-200)]">{value?.toFormat()}</span>
                              ) : !order.isCycleBilled ? (
                                <EditableField
                                  type="number"
                                  value={value?.toUnit()}
                                  formattedValue={value?.toFormat()}
                                  resetPosition="right"
                                  onChange={(value) => {
                                    setLateChargeSummaryRequest((prev) => {
                                      if (prev && typeof value === 'number') {
                                        const idx = prev.items.findIndex((i) => i.id === op.id.toString());
                                        return {
                                          ...prev,
                                          items: [
                                            ...prev.items.filter((item, i) => i !== idx),
                                            {
                                              ...prev.items[idx],
                                              customPrice:
                                                existingAmount!
                                                  .add(
                                                    Dinero({
                                                      amount: round((value || 0) * 100),
                                                      currency,
                                                    }),
                                                  )
                                                  .toUnit() * 100,
                                            },
                                          ],
                                        };
                                      }
                                      return prev;
                                    });
                                  }}
                                />
                              ) : (
                                <div />
                              )}
                            </div>

                            {/* col 6 */}
                            {!order.isCycleBilled ? (
                              <div key={`${op.id}-total`} className="items-center gap-2">
                                {waiveLateCharge ? existingAmount?.toFormat() : lateChargeAmount?.toFormat()}
                              </div>
                            ) : (
                              <div></div>
                            )}
                          </>

                          {/* --------------- row break - start inventory units' rows -------------- */}
                          {op.assignedUnits.map((au) => {
                            return (
                              <>
                                <div className={`ml-4 col-span-6`}>
                                  <div>Unit {au[unitTrackingType]}</div>
                                </div>

                                {/*  ---------------------- row break ---------------- */}
                                {isSelectedForReturn && inventoryReturnRequest && op.product.allowHourTracking ? (
                                  <>
                                    <div></div>
                                    <InventoryReturnHourInput
                                      readingsDisabled={true}
                                      key={au.id}
                                      showLabel={true}
                                      showUnit={false}
                                      labelPosition={'top'}
                                      unitTrackingType={unitTrackingType}
                                      unit={au}
                                      orderProduct={op}
                                      hourReading={
                                        inventoryReturnRequest.hourReadings.find((hr) => hr.unitId === au.id)
                                          ?.hourReading
                                      }
                                      setHourReading={(hr) =>
                                        setInventoryReturnRequest((r) => {
                                          return r
                                            ? {
                                                ...r,
                                                hourReadings: [
                                                  ...r.hourReadings.filter((hr) => hr.unitId !== au.id),
                                                  { unitId: au.id, hourReading: hr },
                                                ],
                                              }
                                            : undefined;
                                        })
                                      }
                                      overage={hourOverages?.find(
                                        (overage) => overage.orderProductId === op.id && overage.unitId === au.id,
                                      )}
                                      overageOverride={hourOverageOverrides?.find(
                                        (overage) => overage.orderProductId === op.id && overage.unitId === au.id,
                                      )}
                                      setOverageOverride={(value) => {
                                        setHourOverageOverrides((existing) => {
                                          return [
                                            ...(existing || []).filter(
                                              (ov) => ov.orderProductId !== op.id && ov.unitId !== au.id,
                                            ),
                                            value,
                                          ];
                                        });
                                      }}
                                      isCalculating={isCalculating}
                                    />
                                    <div></div>

                                    {/*  --------- row break ----------- */}
                                    {op.product.fuelChargeProductId ? (
                                      <>
                                        <div className="col-start-2 col-span-3 gap-4 items-center">
                                          <InventoryReturnFuelChargeInput
                                            readingsDisabled={true}
                                            orderProduct={op}
                                            fuelAmount={fuelCharges.find((fc) => fc.unitId === au.id)?.amount}
                                            setFuelAmount={(fuelAmount) =>
                                              setFuelCharges((r) => {
                                                const existing = r?.find(
                                                  (fc) => fc.orderProductId === op.id && fc.unitId === au.id,
                                                );

                                                return [
                                                  ...(r || []).filter(
                                                    (fc) =>
                                                      fc.orderProductId !== op.id ||
                                                      (fc.orderProductId === op.id && fc.unitId !== au.id),
                                                  ),
                                                  {
                                                    ...existing,
                                                    orderProductId: op.id,
                                                    productId: op.product.fuelChargeProductId,
                                                    unitId: au.id,
                                                    amount: fuelAmount,
                                                    uuid: existing?.uuid ?? uuidv4() + 'fuel',
                                                  },
                                                ];
                                              })
                                            }
                                          />
                                        </div>
                                        <div className="flex flex-col h-full place-content-center">
                                          {op.product.fuelChargeProductId && (
                                            <div className="justify-left">
                                              {/* Compute mult of gas product calculated price * fuel amount */}
                                              {Dinero({
                                                amount:
                                                  lateChargeSummary?.lineItems
                                                    .filter(
                                                      (li) =>
                                                        li.type === 'Product' &&
                                                        li.id.includes('fuel') &&
                                                        li.productId === op.product.fuelChargeProductId,
                                                    )
                                                    .at(0)
                                                    ?.unitPrice.multiply(
                                                      fuelCharges.find((fc) => fc.unitId === au.id)?.amount || 0,
                                                    )
                                                    .getAmount() ?? 0,
                                                currency,
                                              }).toFormat()}
                                            </div>
                                          )}
                                        </div>
                                        {order.isCycleBilled && <div className="col-span-2"></div>}
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                  </>
                                ) : (
                                  <></>
                                )}
                              </>
                            );
                          })}
                          {/* light grey divider */}
                          <div className="col-span-6 border-solid border-[var(--grey-300)]"></div>
                          {/* --------row break after each unit ---------- */}
                        </>
                      );
                    })}
                  {offRentRequest.offRentProducts.length > 0 &&
                    !order.isCycleBilled &&
                    existingSummary?.lineItems
                      .filter(
                        (li) =>
                          !['Product', 'ProductSecurityDeposit', 'SecurityDeposit', 'CustomProduct'].includes(li.type),
                      )
                      .sort((a, b) => financialLineItemTypeToRank(a.type) - financialLineItemTypeToRank(b.type))
                      .map((existingLine, i) => {
                        const lateChargeLine = lateChargeSummary?.lineItems.find((li) => isSameLine(li, existingLine));
                        return (
                          <>
                            <div key={`${i}-name`} className={`items-center gap-2 col-span-3`}>
                              <div>{existingLine.description}</div>
                            </div>
                            <div key={`${i}-original`} className="items-center gap-2">
                              {existingLine.amount.toFormat()}
                            </div>
                            <div key={`${i}-late`} className=" items-center gap-2">
                              {order?.isCycleBilled ? (
                                <div></div>
                              ) : (
                                <>
                                  {lateChargeLine?.type === 'TagPricing' || lateChargeLine?.type === 'Coupon' ? (
                                    waiveLateCharge ? (
                                      <span className="text-[var(--grey-200)]">
                                        {lateChargeLine &&
                                          lateChargeLine.amount.subtract(existingLine.amount).toFormat()}
                                      </span>
                                    ) : (
                                      lateChargeLine.amount.subtract(existingLine.amount).toFormat()
                                    )
                                  ) : waiveLateCharge ? (
                                    <span className="text-[var(--grey-200)]">
                                      {lateChargeLine && lateChargeLine.amount.subtract(existingLine.amount).toFormat()}
                                    </span>
                                  ) : (
                                    <EditableField
                                      type="number"
                                      value={
                                        lateChargeLine && lateChargeLine.amount.subtract(existingLine.amount).toUnit()
                                      }
                                      formattedValue={
                                        lateChargeLine && lateChargeLine.amount.subtract(existingLine.amount).toFormat()
                                      }
                                      resetPosition="right"
                                      onChange={(value) => {
                                        setLateChargeSummaryRequest((prev) => {
                                          if (prev && typeof value === 'number') {
                                            switch (existingLine.type) {
                                              case 'ProductFee': {
                                                const idx =
                                                  prev.customProductFees?.findIndex(
                                                    (cpf) =>
                                                      cpf.productLineItemId === existingLine.productLineItemId &&
                                                      cpf.feeId === existingLine.productFeeId,
                                                  ) || -1;
                                                let customProductFees = prev.customProductFees || [];
                                                if (idx >= 0) {
                                                  customProductFees = [
                                                    ...customProductFees.filter((o, i) => i !== idx),
                                                    {
                                                      ...customProductFees[i],
                                                      amount:
                                                        existingLine.amount
                                                          .add(
                                                            Dinero({
                                                              amount: round((value || 0) * 100),
                                                              currency,
                                                            }),
                                                          )
                                                          .toUnit() * 100,
                                                    },
                                                  ];
                                                } else {
                                                  customProductFees = [
                                                    ...customProductFees,
                                                    {
                                                      productId: existingLine.productId,
                                                      productLineItemId: existingLine.productLineItemId,
                                                      feeId: existingLine.productFeeId,
                                                      amount:
                                                        existingLine.amount
                                                          .add(
                                                            Dinero({
                                                              amount: round(value * 100),
                                                              currency,
                                                            }),
                                                          )
                                                          .toUnit() * 100,
                                                    },
                                                  ];
                                                }
                                                return {
                                                  ...prev,
                                                  customProductFees,
                                                };
                                              }
                                              case 'Fee': {
                                                const idx =
                                                  prev.customFees?.findIndex(
                                                    (cpf) => cpf.feeId === existingLine.feeId,
                                                  ) || -1;
                                                let customFees = prev.customFees || [];
                                                if (idx >= 0) {
                                                  customFees = [
                                                    ...customFees.filter((o, i) => i !== idx),
                                                    {
                                                      ...customFees[i],
                                                      amount:
                                                        existingLine.amount
                                                          .add(
                                                            Dinero({
                                                              amount: round(value * 100),
                                                              currency,
                                                            }),
                                                          )
                                                          .toUnit() * 100,
                                                    },
                                                  ];
                                                } else {
                                                  customFees = [
                                                    ...customFees,
                                                    {
                                                      feeId: existingLine.feeId,
                                                      amount:
                                                        existingLine.amount
                                                          .add(
                                                            Dinero({
                                                              amount: round(value * 100),
                                                              currency,
                                                            }),
                                                          )
                                                          .toUnit() * 100,
                                                    },
                                                  ];
                                                }
                                                return {
                                                  ...prev,
                                                  customFees,
                                                };
                                              }
                                              case 'DamageWaiver': {
                                                return {
                                                  ...prev,
                                                  customDamageWaiver:
                                                    existingLine.amount
                                                      .add(Dinero({ amount: round(value * 100), currency }))
                                                      .toUnit() * 100,
                                                };
                                              }
                                              case 'AdditionalDiscount': {
                                                return {
                                                  ...prev,
                                                  additionalDiscount:
                                                    existingLine.amount
                                                      .add(Dinero({ amount: round(value * 100), currency }))
                                                      .toUnit() * 100,
                                                };
                                              }
                                              case 'Delivery': {
                                                return {
                                                  ...prev,
                                                  customDelivery:
                                                    existingLine.amount
                                                      .add(Dinero({ amount: round(value * 100), currency }))
                                                      .toUnit() * 100,
                                                };
                                              }
                                              case 'Pickup': {
                                                return {
                                                  ...prev,
                                                  customPickup:
                                                    existingLine.amount
                                                      .add(Dinero({ amount: round(value * 100), currency }))
                                                      .toUnit() * 100,
                                                };
                                              }
                                              case 'Tax': {
                                                return {
                                                  ...prev,
                                                  customTax:
                                                    existingLine.amount
                                                      .add(Dinero({ amount: round(value * 100), currency }))
                                                      .toUnit() * 100,
                                                };
                                              }
                                            }
                                          }
                                          return prev;
                                        });
                                      }}
                                    />
                                  )}
                                </>
                              )}
                            </div>
                            <div key={`${i}-total`} className="line-total-div flex flex-row items-center gap-2">
                              {waiveLateCharge ? existingLine?.amount.toFormat() : lateChargeLine?.amount.toFormat()}
                            </div>
                          </>
                        );
                      })}
                  {!order.isCycleBilled &&
                    adhocLineItems.map((li) => {
                      return (
                        <>
                          <div key={`${li.id}-1`} className="col-span-3 items-center gap-2">
                            {li.name}
                          </div>
                          <div key={`${li.id}-4`} className="flex items-center">
                            {Dinero({
                              amount: round(li.total * 100),
                              currency,
                            }).toFormat()}
                          </div>
                          <div key={`${li.id}-5`} className="flex flex-row items-center gap-2"></div>
                          <div key={`${li.id}-6`} className="flex flex-row items-center gap-2">
                            {Dinero({
                              amount: round(li.total * 100),
                              currency,
                            }).toFormat()}
                          </div>
                        </>
                      );
                    })}
                  {!order.isCycleBilled &&
                    hourOverageOverrides &&
                    hourOverageOverrides.some((ov) => ov.overageCharge > 0) && (
                      <>
                        <div className="col-span-3">Hour Overage Tax</div>
                        <div className="col-start-5">{hourOverageTax?.toFormat()}</div>
                        <div />
                      </>
                    )}
                  {!order.isCycleBilled && (
                    <>
                      <div className="flex flex-row items-center gap-2">
                        <div>Total</div>
                      </div>
                      <div></div>
                      <div></div>
                      <div className="flex flex-row items-center gap-2">
                        {adHocLineItemsTotal && existingSummary?.total.add(adHocLineItemsTotal).toFormat()}
                      </div>
                      <div
                        className={classNames('flex flex-row items-center gap-2', {
                          'text-[var(--grey-200)]': waiveLateCharge,
                        })}
                      >
                        {lateChargeTotal?.toFormat()}
                      </div>
                      <div className="flex flex-row items-center gap-2">{total?.toFormat()}</div>
                    </>
                  )}
                  {order.isCycleBilled &&
                  inventoryReturnRequest &&
                  inventoryReturnRequest.orderProductIds.length > 0 &&
                  (fuelCharges.some((fc) => fc.amount > 0) || hourOverages?.some((ov) => ov.overageCharge > 0)) ? (
                    <>
                      <div className="col-span-3"></div>
                      <div>Subtotal</div>
                      <div>{cycleBillingReturnTotals.subtotal.toFormat()}</div>
                      <div className="col-span-3"></div>
                      <div>Tax</div>
                      <div>{cycleBillingReturnTotals.tax.toFormat()}</div>
                      <div className="col-span-3"></div>
                      <div>Total</div>
                      <div>{cycleBillingReturnTotals.total.toFormat()}</div>
                    </>
                  ) : (
                    <></>
                  )}
                </div>
              </div>

              {(existingSummary && lateChargeSummary && lateChargeSummary.total.greaterThan(existingSummary.total)) ||
              (invoice && balance.toUnit() > 0) ? (
                <div className="mt-4 flex flex-column gap-2">
                  <h6 className="m-0">Payment</h6>
                  <div className="flex gap-2 text-[0.9rem]">
                    <Toggle
                      onClick={() => {
                        setOffRentWithPayment((prev) => !prev);
                      }}
                      checked={offRentWithPayment}
                    />
                    Off Rent with Payment
                  </div>
                  {offRentWithPayment && (
                    <StatusTransitionPayment
                      order={order}
                      paymentMethod={paymentMethod}
                      setPaymentMethod={setPaymentMethod}
                      total={
                        (order.isCycleBilled && inventoryReturnRequest
                          ? cycleBillingReturnTotals.total.toUnit()
                          : total?.toUnit()) || 0
                      }
                      paymentTotal={paymentsTotal.toUnit()}
                      amount={amount}
                      setAmount={setAmount}
                      defaultPercentage={100}
                      payments={payments}
                      setPayments={setPayments}
                      facilitate={facilitate}
                      onComplete={postPayment}
                    />
                  )}
                </div>
              ) : (
                !order.isCycleBilled && <div className="mt-4">Order has been fully paid</div>
              )}
              <ValidationError
                className={classNames({ hidden: orderValidationMessages.length === 0 })}
                style={{ maxWidth: '342px' }}
              >
                <ul>
                  {orderValidationMessages.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </ValidationError>
            </div>
          </div>
        )
      )}
    </Modal>
  );
};
